// packages/sdk/src/components/FormRenderer.tsx

import React, { useState } from 'react';
import type { FieldConfig } from '../types';

// --- Types ---

export type FieldValue = string | number | boolean | string[];

export interface FormValues {
  [fieldId: string]: FieldValue;
}

interface FormRendererProps {
  fields: FieldConfig[];
  values: FormValues;
  onChange: (fieldId: string, value: FieldValue) => void;
  disabled?: boolean;
  primaryColor?: string;
}

// --- Shared field wrapper ---

const FieldWrapper: React.FC<{
  label: string;
  fieldId: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, fieldId, required, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label
      htmlFor={fieldId}
      style={{
        fontSize: '13px',
        fontWeight: 500,
        color: '#374151',
      }}
    >
      {label}
      {required && (
        <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>
      )}
    </label>
    {children}
  </div>
);

// --- Field: text / textarea ---

const TextField: React.FC<{
  field: FieldConfig;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  multiline?: boolean;
}> = ({ field, value, onChange, disabled, multiline }) => {
  const shared: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    opacity: disabled ? 0.6 : 1,
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  };

  if (multiline) {
    return (
      <textarea
        id={field.id}
        value={value}
        placeholder={field.placeholder}
        required={field.required}
        disabled={disabled}
        rows={4}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...shared, resize: 'vertical', minHeight: '88px' }}
        onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
        onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
      />
    );
  }

  return (
    <input
      id={field.id}
      type={field.id.includes('email') ? 'email' : 'text'}
      value={value}
      placeholder={field.placeholder}
      required={field.required}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={shared}
      onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
      onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
    />
  );
};

// --- Field: star rating (1–5) ---

const RatingField: React.FC<{
  field: FieldConfig;
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
  primaryColor?: string;
}> = ({ field, value, onChange, disabled, primaryColor = '#fbbf24' }) => (
  <div
    role="group"
    aria-label={field.label}
    style={{ display: 'flex', gap: '6px' }}
  >
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={disabled}
        aria-label={`Rate ${star} stars`}
        aria-pressed={value === star}
        onClick={() => onChange(star)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '26px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          padding: '0',
          color: value !== null && value >= star ? primaryColor : '#d1d5db',
          transition: 'transform 0.15s, color 0.15s',
          opacity: disabled ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
          if (!disabled) e.currentTarget.style.transform = 'scale(1.2)';
        }}
        onMouseLeave={(e) => {
          if (!disabled) e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        ★
      </button>
    ))}
  </div>
);

// --- Field: NPS (0–10) ---

const NpsField: React.FC<{
  field: FieldConfig;
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
  primaryColor?: string;
}> = ({ field, value, onChange, disabled }) => (
  <div>
    <div
      style={{
        display: 'flex',
        gap: '4px',
        flexWrap: 'wrap',
      }}
    >
      {Array.from({ length: 11 }, (_, i) => {
        const isSelected = value === i;
        const baseColor =
          i >= 9 ? '#16a34a' : i >= 7 ? '#d97706' : '#dc2626';
        return (
          <button
            key={i}
            type="button"
            disabled={disabled}
            aria-label={`Score ${i}`}
            aria-pressed={isSelected}
            onClick={() => onChange(i)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: `1.5px solid ${isSelected ? baseColor : '#e5e7eb'}`,
              backgroundColor: isSelected ? baseColor : '#ffffff',
              color: isSelected ? '#ffffff' : '#374151',
              fontSize: '13px',
              fontWeight: 600,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              transition: 'all 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!disabled && !isSelected) {
                e.currentTarget.style.borderColor = baseColor;
                e.currentTarget.style.backgroundColor = `${baseColor}10`;
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled && !isSelected) {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            {i}
          </button>
        );
      })}
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '6px',
        fontSize: '11px',
        color: '#9ca3af',
      }}
    >
      <span>{field.lowLabel ?? 'Not likely'}</span>
      <span>{field.highLabel ?? 'Very likely'}</span>
    </div>
  </div>
);

// --- Field: CSAT (emoji scale) ---

const DEFAULT_EMOJIS: [string, string, string, string, string] = [
  '😞', '😕', '😐', '😊', '😄',
];

const CsatField: React.FC<{
  field: FieldConfig;
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
}> = ({ field, value, onChange, disabled }) => {
  const emojis = field.emojiScale ?? DEFAULT_EMOJIS;
  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {emojis.map((emoji: string, i: number) => {
        const score = i + 1;
        const isSelected = value === score;
        return (
          <button
            key={score}
            type="button"
            disabled={disabled}
            aria-label={`Score ${score}`}
            aria-pressed={isSelected}
            onClick={() => onChange(score)}
            style={{
              background: 'none',
              border: `1.5px solid ${isSelected ? '#6b7280' : 'transparent'}`,
              borderRadius: '12px',
              padding: '6px 8px',
              fontSize: '28px',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : isSelected ? 1 : 0.5,
              transform: isSelected ? 'scale(1.2)' : 'scale(1)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!disabled) e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = isSelected ? '1' : '0.5';
              }
            }}
          >
            {emoji}
          </button>
        );
      })}
    </div>
  );
};

// --- Field: select (dropdown) ---

const SelectField: React.FC<{
  field: FieldConfig;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ field, value, onChange, disabled }) => (
  <select
    id={field.id}
    value={value}
    disabled={disabled}
    required={field.required}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%',
      padding: '8px 10px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: '#ffffff',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      outline: 'none',
    }}
  >
    <option value="">Select an option…</option>
    {(field.options ?? []).map((opt: string) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

// --- Field: multiselect (checkbox group) ---

const MultiSelectField: React.FC<{
  field: FieldConfig;
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}> = ({ field, value, onChange, disabled }) => {
  const toggle = (opt: string) => {
    const next = value.includes(opt)
      ? value.filter((v) => v !== opt)
      : [...value, opt];
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {(field.options ?? []).map((opt: string) => (
        <label
          key={opt}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#374151',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={value.includes(opt)}
            disabled={disabled}
            onChange={() => toggle(opt)}
            style={{ width: '15px', height: '15px', cursor: 'inherit' }}
          />
          {opt}
        </label>
      ))}
    </div>
  );
};

// --- Field: boolean (yes / no) ---

const BooleanField: React.FC<{
  field: FieldConfig;
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  primaryColor?: string;
}> = ({ value, onChange, disabled, primaryColor = '#3b82f6' }) => (
  <div style={{ display: 'flex', gap: '10px' }}>
    {[
      { label: 'Yes', val: true },
      { label: 'No', val: false },
    ].map(({ label, val }) => {
      const isSelected = value === val;
      return (
        <button
          key={label}
          type="button"
          disabled={disabled}
          onClick={() => onChange(val)}
          style={{
            padding: '8px 24px',
            borderRadius: '8px',
            border: `1.5px solid ${isSelected ? primaryColor : '#e5e7eb'}`,
            backgroundColor: isSelected ? primaryColor : '#ffffff',
            color: isSelected ? '#ffffff' : '#374151',
            fontSize: '14px',
            fontWeight: 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'all 0.15s',
          }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

// --- FormRenderer ---

export const FormRenderer: React.FC<FormRendererProps> = ({
  fields,
  values,
  onChange,
  disabled = false,
  primaryColor,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {fields.map((field) => {
        const value = values[field.id];

        return (
          <FieldWrapper
            key={field.id}
            label={field.label}
            fieldId={field.id}
            required={field.required}
          >
            {field.type === 'textarea' && (
              <TextField
                field={field}
                value={(value as string) ?? ''}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
                multiline
              />
            )}

            {field.type === 'text' && (
              <TextField
                field={field}
                value={(value as string) ?? ''}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
              />
            )}

            {field.type === 'rating' && (
              <RatingField
                field={field}
                value={(value as number) ?? null}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
                primaryColor={primaryColor}
              />
            )}

            {field.type === 'nps' && (
              <NpsField
                field={field}
                value={(value as number) ?? null}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
                primaryColor={primaryColor}
              />
            )}

            {field.type === 'csat' && (
              <CsatField
                field={field}
                value={(value as number) ?? null}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
              />
            )}

            {field.type === 'select' && (
              <SelectField
                field={field}
                value={(value as string) ?? ''}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
              />
            )}

            {field.type === 'multiselect' && (
              <MultiSelectField
                field={field}
                value={(value as string[]) ?? []}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
              />
            )}

            {field.type === 'boolean' && (
              <BooleanField
                field={field}
                value={value === undefined ? null : (value as boolean)}
                onChange={(v) => onChange(field.id, v)}
                disabled={disabled}
                primaryColor={primaryColor}
              />
            )}
          </FieldWrapper>
        );
      })}
    </div>
  );
};

// --- Hook: manages form values and validation ---

export function useFormState(fields: FieldConfig[]) {
  const [values, setValues] = useState<FormValues>({});

  const handleChange = (fieldId: string, value: FieldValue) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const validate = (): string | null => {
    for (const field of fields) {
      if (!field.required) continue;
      const val = values[field.id];
      if (
        val === undefined ||
        val === null ||
        val === '' ||
        (Array.isArray(val) && val.length === 0)
      ) {
        return `"${field.label}" is required`;
      }
    }
    return null;
  };

  const reset = () => setValues({});

  return { values, handleChange, validate, reset };
}